import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { MoreVertical, Pencil, Trash2 } from "lucide-react";

/**
 * ActionMenu — menu ⋮ réutilisable pour tous les tableaux
 *
 * Props :
 *   rowId       : identifiant unique de la ligne
 *   openMenu    : state du parent (id ouvert ou null)
 *   setOpenMenu : setter du parent
 *   onEdit      : () => void  — callback Modifier
 *   onDelete    : () => void  — callback Supprimer
 *   extraActions: [{ icon: <Icon />, label: "...", onClick: fn, red: false }]
 *                 actions supplémentaires optionnelles (ex: Consulter, Annuler…)
 */
export default function ActionMenu({
  rowId,
  openMenu,
  setOpenMenu,
  onEdit,
  onDelete,
  extraActions = [],
}) {
  const isOpen = openMenu === rowId;
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Calcule la position du dropdown par rapport au bouton
  const updatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const menuHeight = 120; // estimation hauteur menu
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < menuHeight && rect.top > menuHeight;

    setPos({
      top: openAbove ? rect.top - menuHeight : rect.bottom + 4,
      left: Math.min(rect.right - 160, window.innerWidth - 170),
    });
  }, []);

  // Fermeture click extérieur
  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const handler = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        setOpenMenu(null);
      }
    };
    const onScroll = () => setOpenMenu(null);
    document.addEventListener("mousedown", handler);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", handler);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [isOpen, setOpenMenu, updatePosition]);

  return (
    <>
      {/* Bouton ⋮ */}
      <button
        ref={btnRef}
        onClick={(e) => {
          e.stopPropagation();
          setOpenMenu(isOpen ? null : rowId);
        }}
        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-gray-100 transition-colors"
      >
        <MoreVertical size={16} />
      </button>

      {/* Dropdown via portail — rendu en dehors de la table */}
      {isOpen && createPortal(
        <div
          ref={menuRef}
          className="fixed z-[9999] bg-white border border-gray-100 rounded-xl shadow-lg py-1 min-w-[160px]"
          style={{ top: pos.top, left: pos.left }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Actions supplémentaires avant Modifier */}
          {extraActions.filter(a => a.position === "before").map((a, i) => (
            <button
              key={i}
              onClick={() => { a.onClick(); setOpenMenu(null); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                a.red ? "text-red-600" : "text-slate-700"
              }`}
            >
              {a.icon} {a.label}
            </button>
          ))}

          {/* Modifier */}
          {onEdit && (
            <button
              onClick={() => { onEdit(); setOpenMenu(null); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-gray-50 transition-colors"
            >
              <Pencil size={15} className="text-slate-400" /> Modifier
            </button>
          )}

          {/* Actions supplémentaires après Modifier */}
          {extraActions.filter(a => a.position !== "before").map((a, i) => (
            <button
              key={i}
              onClick={() => { a.onClick(); setOpenMenu(null); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                a.red ? "text-red-600" : "text-slate-700"
              }`}
            >
              {a.icon} {a.label}
            </button>
          ))}

          {/* Séparateur avant Supprimer */}
          {onDelete && (onEdit || extraActions.length > 0) && (
            <div className="border-t border-gray-100 my-1" />
          )}

          {/* Supprimer */}
          {onDelete && (
            <button
              onClick={() => { onDelete(); setOpenMenu(null); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash2 size={15} className="text-red-400" /> Supprimer
            </button>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
